/**
 * TableModeBannerIsland — banner sticky no topo + lógica de mesa/comanda.
 *
 * Substitui o bloco V1 (delivery_app.html:12950-13095) que lidava com
 * STORE_MODE.type 'table_service' e 'comanda'.
 *
 * Dois modos:
 *  1. MESA (`type='table_service'`, `table=N`): QR code colado na mesa.
 *     Boot chama `/api/delivery/check-table` pra verificar se a mesa
 *     está ocupada por OUTRO device. Se ocupada → modal bloqueante +
 *     redireciona pro modo delivery (`/app/<slug>`). Se OK → banner
 *     indigo sticky "MESA N".
 *
 *  2. COMANDA (`type='comanda'`, `comanda=N`): comanda numerada.
 *     Cliente pode estar em qualquer mesa. Pergunta a mesa atual no
 *     boot (cacheia em sessionStorage). Banner roxo "COMANDA N | MESA X"
 *     com botão "Trocar" pra cliente mudar de mesa. Modal próprio
 *     (sem Swal).
 *
 * Pra modo `delivery` (default), o componente NÃO renderiza nada.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t, useI18n } from '../i18n';

type StoreModeType = 'delivery' | 'table_service' | 'comanda';

interface StoreMode {
  type?: StoreModeType;
  table?: string;
  comanda?: string;
  /** Config da loja: mesa OBRIGATÓRIA no QR da comanda (default false). */
  table_required?: boolean;
}

declare global {
  interface Window {
    STORE_MODE?: StoreMode;
    STORE_SLUG?: string;
    promptChangeTable?: () => void;
  }
}

interface TableCheckResp {
  available?: boolean;
  message?: string;
}

async function checkTableAvailability(slug: string, tableNum: string): Promise<TableCheckResp> {
  let devId: string | null = null;
  try {
    devId = localStorage.getItem('aliv_device_id');
    if (!devId) {
      devId = 'dev_' + Math.random().toString(36).slice(2, 11);
      localStorage.setItem('aliv_device_id', devId);
    }
  } catch { /* */ }

  const token = (() => {
    try { return localStorage.getItem('wabiz_token'); } catch { return null; }
  })();
  const headers: HeadersInit = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const url = `/api/delivery/check-table?store_slug=${encodeURIComponent(slug)}&table_number=${encodeURIComponent(tableNum)}&device_id=${encodeURIComponent(devId || '')}`;
    const r = await fetch(url, { headers });
    return (await r.json()) as TableCheckResp;
  } catch {
    return { available: false, message: t('Erro de conexão ao validar mesa.') };
  }
}

export function TableModeBannerIsland() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const mode = (window.STORE_MODE || {}) as StoreMode;
  const slug = window.STORE_SLUG || '';

  // Estado do banner — só renderiza se modo é mesa/comanda.
  const [tableNum, setTableNum] = useState<string>(mode.table || '');
  // Bloqueio de mesa ocupada (modo table_service).
  const [blockedMsg, setBlockedMsg] = useState<string | null>(null);
  // Modal de trocar mesa (modo comanda).
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptInput, setPromptInput] = useState<string>('');
  // Modal opcional pedindo o nome do cliente. Aparece UMA vez por sessão,
  // após confirmar mesa/comanda. Cliente pode pular sem preencher.
  const [namePromptOpen, setNamePromptOpen] = useState(false);
  const [nameInput, setNameInput] = useState<string>('');
  const initCheckRanRef = useRef(false);
  const namePromptShownRef = useRef(false);

  // Mostra o prompt de nome só se ainda não pediu nesta sessão E cliente
  // não tem nome em USER (logado) nem em sessionStorage.
  function maybeAskName() {
    if (namePromptShownRef.current) return;
    namePromptShownRef.current = true;
    const u = (window as Window & { USER?: { name?: string } | null }).USER;
    if (u?.name?.trim()) return; // logado já tem nome
    try {
      const saved = sessionStorage.getItem('aliv_guest_name');
      if (saved && saved.trim()) return; // já preencheu antes
    } catch { /* */ }
    // Atraso curto pra não competir com outros prompts (mesa check,
    // comanda prompt). UX: mesa → nome aparece DEPOIS.
    setTimeout(() => setNamePromptOpen(true), 500);
  }

  const isTableMode = mode.type === 'table_service' && !!mode.table;
  const isComandaMode = mode.type === 'comanda' && !!mode.comanda;
  // Mesa obrigatória (config da loja): o modal não fecha sem número e o
  // "Não estou em mesa" some. Só vale no modo comanda.
  const tableRequired = isComandaMode && !!mode.table_required;
  const [promptError, setPromptError] = useState(false);

  // 1. Boot do MODO MESA — check-table, bloqueia se ocupada.
  useEffect(() => {
    if (!isTableMode || initCheckRanRef.current) return;
    initCheckRanRef.current = true;
    (async () => {
      const res = await checkTableAvailability(slug, mode.table!);
      if (!res.available) {
        setBlockedMsg(res.message || 'Mesa indisponível no momento.');
      } else {
        // Mesa OK — oferece prompt opcional de nome (não obrigatório).
        maybeAskName();
      }
    })();
  }, [isTableMode, slug, mode.table]);

  // 2. Boot do MODO COMANDA — pergunta a mesa do cliente (cacheia
  // em sessionStorage por comanda; se já tem cache, usa).
  useEffect(() => {
    if (!isComandaMode) return;
    let cached: string | null = null;
    try {
      cached = sessionStorage.getItem(`mesa_da_comanda_${mode.comanda}`);
    } catch { /* */ }
    // Obrigatório: cache VAZIO (cliente clicou "Não estou em mesa" antes da
    // loja ligar a exigência, ou sessão antiga) não conta como respondido.
    if (cached !== null && !(tableRequired && !cached.trim())) {
      setTableNum(cached);
      if (window.STORE_MODE) window.STORE_MODE.table = cached;
      // Já confirmou mesa em sessão anterior — pergunta nome também.
      maybeAskName();
    } else {
      // Sem cache — abre modal pedindo. (PromptOpen=true mostra o input.)
      setPromptOpen(true);
    }
  }, [isComandaMode, mode.comanda]);

  // Expõe window.promptChangeTable pra paridade com V1 (alguns lugares
  // legados podem chamar). Hoje o botão "Trocar" do banner chama
  // diretamente openPrompt().
  useEffect(() => {
    if (!isComandaMode) return;
    window.promptChangeTable = () => setPromptOpen(true);
    return () => { delete window.promptChangeTable; };
  }, [isComandaMode]);

  function confirmPrompt(value: string) {
    const v = value.replace(/\D/g, '').slice(0, 6); // só dígitos, max 6
    // Obrigatório: sem número o modal não fecha — mostra o aviso inline.
    if (tableRequired && !v) {
      setPromptError(true);
      return;
    }
    setPromptError(false);
    try {
      sessionStorage.setItem(`mesa_da_comanda_${mode.comanda}`, v);
    } catch { /* */ }
    setTableNum(v);
    if (window.STORE_MODE) window.STORE_MODE.table = v;
    setPromptOpen(false);
    setPromptInput('');
    maybeAskName();
  }

  function cancelPrompt() {
    // "Não estou em mesa" — limpa o cache da comanda
    try {
      sessionStorage.setItem(`mesa_da_comanda_${mode.comanda}`, '');
    } catch { /* */ }
    setTableNum('');
    if (window.STORE_MODE) window.STORE_MODE.table = '';
    setPromptOpen(false);
    setPromptInput('');
    maybeAskName();
  }

  function saveGuestName(name: string) {
    const clean = name.trim().slice(0, 50);
    if (clean) {
      try { sessionStorage.setItem('aliv_guest_name', clean); } catch { /* */ }
      // Também popula window.USER se estava null (paridade greeting V1).
      const u = (window as Window & { USER?: { name?: string; phone?: string } | null }).USER;
      if (!u) {
        (window as Window & { USER?: { name?: string; phone?: string } | null }).USER = { name: clean };
        try { (window as Window & { updateGreeting?: () => void }).updateGreeting?.(); } catch { /* */ }
      }
    }
    setNamePromptOpen(false);
    setNameInput('');
  }

  function redirectToDelivery() {
    window.location.href = `/app/${encodeURIComponent(slug)}`;
  }

  // Se modo delivery padrão, não renderiza nada.
  if (!isTableMode && !isComandaMode) return null;

  return (
    <>
      {/* Banner sticky no topo — adicionado via portal pro body pra
          ficar visível ANTES do header do app, com z alto pra não ser
          coberto por nada do flow normal. */}
      {isTableMode && !blockedMsg && createPortal(
        <div className="fixed top-0 inset-x-0 z-[90] bg-indigo-600 text-white text-center py-2 font-bold text-sm shadow-md">
          <i className="fas fa-chair mr-1" /> Você está na MESA {mode.table}
        </div>,
        document.body,
      )}

      {isComandaMode && createPortal(
        <div
          className="fixed top-0 inset-x-0 z-[90] text-center py-2 font-bold text-sm shadow-md flex justify-center items-center gap-2 text-white"
          style={{ backgroundColor: '#9333ea' }}
        >
          <span>
            <i className="fas fa-ticket-alt mr-1" /> COMANDA {mode.comanda}
            {tableNum && (
              <span>
                <span style={{ opacity: 0.75, margin: '0 4px' }}>|</span>
                MESA {tableNum}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => setPromptOpen(true)}
            className="ml-2 px-2.5 py-1 rounded text-[10px] font-bold transition"
            style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.4)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)')}
          >
            <i className="fas fa-exchange-alt mr-1" /> {t('Trocar')}
          </button>
        </div>,
        document.body,
      )}

      {/* Modal bloqueante — mesa ocupada (table_service). */}
      {blockedMsg && createPortal(
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center p-4 bg-black/70"
          style={{ zIndex: 2147483646 }}
        >
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <i className="fas fa-chair text-2xl" />
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 mb-2">{t('Mesa ocupada 😕')}</h3>
            <p className="text-sm text-gray-600 mb-5">{blockedMsg}</p>
            <button
              type="button"
              onClick={redirectToDelivery}
              className="w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm shadow-lg"
            >
              {t('Entendi')}
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* Modal pra trocar/informar mesa (comanda mode). */}
      {promptOpen && createPortal(
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center p-4 bg-black/70"
          style={{ zIndex: 2147483646 }}
        >
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
              <i className="fas fa-map-marker-alt text-2xl" />
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 text-center mb-1">
              {t('Onde você está? 📍')}
            </h3>
            <p className="text-sm text-gray-600 text-center mb-4">
              {t('Sua comanda é a')} <b>nº {mode.comanda}</b>. Em qual mesa você está?
              {tableRequired ? (
                <span className="block text-xs text-purple-600 font-bold mt-1">(obrigatório — o garçom leva o pedido até você)</span>
              ) : (
                <span className="block text-xs text-gray-400 mt-1">(opcional — ajuda o garçom a te achar)</span>
              )}
            </p>
            <input
              type="text"
              inputMode="numeric"
              value={promptInput}
              onChange={(e) => { setPromptInput(e.target.value.replace(/\D/g, '').slice(0, 6)); setPromptError(false); }}
              autoFocus
              placeholder={t("Número da mesa")}
              className={`w-full border-2 rounded-xl px-3 py-3 text-center text-xl font-extrabold tabular-nums outline-none mb-1 ${promptError ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-purple-500'}`}
            />
            {promptError && (
              <p className="text-xs text-red-500 font-bold text-center mb-3">{t('Informe o número da mesa pra continuar.')}</p>
            )}
            <div className={`flex gap-2 ${promptError ? '' : 'mt-3'}`}>
              {!tableRequired && (
                <button
                  type="button"
                  onClick={cancelPrompt}
                  className="flex-1 py-2.5 rounded-xl text-gray-700 font-bold text-sm border border-gray-200 hover:bg-gray-50"
                >
                  {t('Não estou em mesa')}
                </button>
              )}
              <button
                type="button"
                onClick={() => confirmPrompt(promptInput)}
                className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm"
              >
                {t('Confirmar')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Modal opcional pedindo o nome — UX iFood/Rappi pra mesa.
          Não obriga (botão Pular). Salva em sessionStorage.aliv_guest_name
          + popula window.USER se vazio. CheckoutIsland lê esse valor pra
          customer_name. */}
      {namePromptOpen && createPortal(
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center p-4 bg-black/70"
          style={{ zIndex: 2147483646 }}
        >
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">
              <i className="fas fa-user text-2xl" />
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 text-center mb-1">
              {t('Como podemos te chamar?')}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-4">
              {t('Pra deixar seu atendimento mais pessoal.')} <span className="text-gray-400">(opcional)</span>
            </p>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              autoFocus
              maxLength={50}
              placeholder={t("Seu nome")}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveGuestName(nameInput);
              }}
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-3 text-base font-medium focus:border-rose-500 outline-none mb-4"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setNamePromptOpen(false); setNameInput(''); }}
                className="flex-1 py-2.5 rounded-xl text-gray-700 font-bold text-sm border border-gray-200 hover:bg-gray-50"
              >
                {t('Pular')}
              </button>
              <button
                type="button"
                onClick={() => saveGuestName(nameInput)}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm"
              >
                {t('Salvar')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Spacer pra não cobrir conteúdo abaixo do header sticky */}
      {(isTableMode && !blockedMsg) || isComandaMode ? (
        <div style={{ height: '36px' }} aria-hidden />
      ) : null}
    </>
  );
}
